<template>
  <div class="dynamic-form-page">
    <div v-if="loading" class="loading-container">
      <div class="loader"></div>
      <p>Loading form...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <h2>Form Not Found</h2>
      <p>{{ error }}</p>
      <button @click="$router.push('/')" class="btn-home">Go to Homepage</button>
    </div>

    <div v-else-if="formData" class="form-container">
      <div v-if="submitted" class="success-container">
        <div class="success-animation">
          <div class="checkmark">✓</div>
        </div>
        <h2>Thank You!</h2>
        <p>Your response has been submitted successfully.</p>
        <button @click="resetForm" class="btn-submit-another">Submit Another Response</button>
      </div>

      <div v-else class="form-content">
        <div class="form-header">
          <h1 class="form-title">{{ formData.title }}</h1>
          <p v-if="formData.description" class="form-description">{{ formData.description }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="questions-form">
          <div v-for="(question, index) in formData.questions" :key="index" class="question-group">
            <label class="question-label">
              {{ question.label }}
              <span v-if="question.required" class="required">*</span>
            </label>

            <!-- Short Text -->
            <input
              v-if="question.type === 'text'"
              v-model="responses[index]"
              type="text"
              class="input-field"
              :required="question.required"
            />

            <!-- Long Text -->
            <textarea
              v-else-if="question.type === 'textarea'"
              v-model="responses[index]"
              class="textarea-field"
              :required="question.required"
            ></textarea>

            <!-- Email -->
            <input
              v-else-if="question.type === 'email'"
              v-model="responses[index]"
              type="email"
              class="input-field"
              :required="question.required"
            />

            <!-- Phone -->
            <input
              v-else-if="question.type === 'tel'"
              v-model="responses[index]"
              type="tel"
              class="input-field"
              :required="question.required"
            />

            <!-- Radio Buttons -->
            <div v-else-if="question.type === 'radio'" class="options-container">
              <label
                v-for="(option, optIndex) in question.options"
                :key="optIndex"
                class="radio-label"
              >
                <input
                  type="radio"
                  :name="`question-${index}`"
                  :value="option"
                  v-model="responses[index]"
                  :required="question.required"
                />
                <span>{{ option }}</span>
              </label>
            </div>

            <!-- Checkboxes -->
            <div v-else-if="question.type === 'checkbox'" class="options-container">
              <label
                v-for="(option, optIndex) in question.options"
                :key="optIndex"
                class="checkbox-label"
              >
                <input
                  type="checkbox"
                  :value="option"
                  @change="updateCheckbox(index, option, $event)"
                />
                <span>{{ option }}</span>
              </label>
              <div v-if="question.required && validationErrors[index]" class="error-message">
                {{ validationErrors[index] }}
              </div>
            </div>

            <!-- Dropdown -->
            <select
              v-else-if="question.type === 'select'"
              v-model="responses[index]"
              class="select-field"
              :required="question.required"
            >
              <option value="" disabled>Choose an option</option>
              <option v-for="(option, optIndex) in question.options" :key="optIndex" :value="option">
                {{ option }}
              </option>
            </select>
          </div>

          <div v-if="submitError" class="error-message submit-error">
            {{ submitError }}
          </div>

          <button type="submit" class="btn-submit" :disabled="submitting">
            <span v-if="submitting">Submitting...</span>
            <span v-else>Submit</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '../firebase'
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore'

export default {
  name: 'DynamicFormPage',
  data() {
    return {
      formData: null,
      loading: true,
      error: null,
      responses: {},
      validationErrors: {},
      submitted: false,
      submitting: false,
      submitError: null
    }
  },
  async mounted() {
    await this.loadForm()
  },
  methods: {
    async loadForm() {
      try {
        this.loading = true
        const formId = this.$route.params.id
        
        if (!formId) {
          this.error = 'No form ID provided'
          return
        }

        const formDoc = await getDoc(doc(db, 'forms', formId))
        
        if (!formDoc.exists()) {
          this.error = 'This form does not exist or has been deleted.'
          return
        }

        this.formData = {
          id: formDoc.id,
          ...formDoc.data()
        }

        // Initialize responses object
        this.formData.questions.forEach((_, index) => {
          this.responses[index] = this.formData.questions[index].type === 'checkbox' ? [] : ''
        })
      } catch (error) {
        console.error('Error loading form:', error)
        this.error = 'An error occurred while loading the form.'
      } finally {
        this.loading = false
      }
    },
    updateCheckbox(questionIndex, option, event) {
      if (!Array.isArray(this.responses[questionIndex])) {
        this.responses[questionIndex] = []
      }

      if (event.target.checked) {
        this.responses[questionIndex].push(option)
      } else {
        const index = this.responses[questionIndex].indexOf(option)
        if (index > -1) {
          this.responses[questionIndex].splice(index, 1)
        }
      }

      // Clear validation error when user selects an option
      if (this.validationErrors[questionIndex] && this.responses[questionIndex].length > 0) {
        this.$delete(this.validationErrors, questionIndex)
      }
    },
    validateForm() {
      this.validationErrors = {}
      let isValid = true

      this.formData.questions.forEach((question, index) => {
        if (question.required) {
          if (question.type === 'checkbox') {
            if (!this.responses[index] || this.responses[index].length === 0) {
              this.validationErrors[index] = 'Please select at least one option'
              isValid = false
            }
          }
        }
      })

      return isValid
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        this.submitError = 'Please fill in all required fields'
        return
      }

      try {
        this.submitting = true
        this.submitError = null

        // Prepare submission data
        const submission = {
          formId: this.formData.id,
          formTitle: this.formData.title,
          responses: {},
          submittedAt: serverTimestamp()
        }

        // Map responses to question labels
        this.formData.questions.forEach((question, index) => {
          submission.responses[question.label] = this.responses[index]
        })

        // Save to Firestore
        await addDoc(collection(db, 'formResponses'), submission)

        this.submitted = true
      } catch (error) {
        console.error('Error submitting form:', error)
        this.submitError = 'An error occurred while submitting the form. Please try again.'
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      this.submitted = false
      this.formData.questions.forEach((_, index) => {
        this.responses[index] = this.formData.questions[index].type === 'checkbox' ? [] : ''
      })
      this.validationErrors = {}
      this.submitError = null
    }
  }
}
</script>

<style scoped>
.dynamic-form-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 20px;
}

.loading-container,
.error-container {
  max-width: 600px;
  margin: 80px auto;
  background: white;
  border-radius: 12px;
  padding: 60px 40px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
}

.loader {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #673ab7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.error-container h2 {
  font-size: 24px;
  color: #1a1a1a;
  margin: 0 0 12px 0;
}

.error-container p {
  font-size: 16px;
  color: #666;
  margin: 0 0 24px 0;
}

.btn-home {
  padding: 12px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-home:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(103, 58, 183, 0.4);
}

.form-container {
  max-width: 700px;
  margin: 0 auto;
}

.success-container {
  background: white;
  border-radius: 12px;
  padding: 60px 40px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
}

.success-animation {
  margin-bottom: 30px;
}

.checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  animation: scaleIn 0.5s ease-out;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-container h2 {
  font-size: 28px;
  color: #1a1a1a;
  margin: 0 0 12px 0;
}

.success-container p {
  font-size: 16px;
  color: #666;
  margin: 0 0 30px 0;
}

.btn-submit-another {
  padding: 12px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-submit-another:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(103, 58, 183, 0.4);
}

.form-content {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
}

.form-header {
  margin-bottom: 40px;
  padding-bottom: 24px;
  border-bottom: 2px solid #f0f0f0;
}

.form-title {
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px 0;
}

.form-description {
  font-size: 16px;
  color: #666;
  margin: 0;
  line-height: 1.6;
}

.questions-form {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.question-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.question-label {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.required {
  color: #d32f2f;
  margin-left: 4px;
}

.input-field,
.textarea-field,
.select-field {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.2s;
}

.input-field:focus,
.textarea-field:focus,
.select-field:focus {
  outline: none;
  border-color: #673ab7;
  box-shadow: 0 0 0 3px rgba(103, 58, 183, 0.1);
}

.textarea-field {
  min-height: 120px;
  resize: vertical;
}

.options-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label,
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #444;
  cursor: pointer;
  padding: 10px;
  border-radius: 6px;
  transition: all 0.2s;
}

.radio-label:hover,
.checkbox-label:hover {
  background: #f8f5fb;
}

.radio-label input[type="radio"],
.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.error-message {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 14px;
  color: #856404;
  margin-top: 8px;
}

.submit-error {
  margin-top: 0;
}

.btn-submit {
  padding: 16px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 10px;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(103, 58, 183, 0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .dynamic-form-page {
    padding: 20px 12px;
  }

  .form-content {
    padding: 24px 20px;
  }

  .form-title {
    font-size: 24px;
  }

  .success-container {
    padding: 40px 24px;
  }
}
</style>
